// @flow
import { EventActionTypes } from './actionTypes';
import type { FluxStandardAction } from 'modules/helper';
import type { Event } from 'types/event';
import type { ActiveEventFilters, EventFilters } from 'types/filter';
import type { GalleryImageType } from 'types/common';

const getInitialActiveFilterState = (): ActiveEventFilters => {
  return {
    venue: [],
    genre: [],
    city: [],
    artist: [],
  };
};

const getInitialUpcomingActiveFilterState = (): ActiveEventFilters => {
  return {
    venue: [],
    genre: [],
    city: [],
    artist: [],
  };
};

const toggleArrayItem = (value: number, arr: Array<number>): Array<number> => {
  const filterSet = new Set(arr);
  if (filterSet.has(value)) {
    filterSet.delete(value);
  } else {
    filterSet.add(value);
  }
  return Array.from(filterSet);
};

const updateActiveFilters = (
  payload: { type: string, value: number },
  filters
) => {
  const newFilters = { ...filters };
  const value = payload.value;
  switch (payload.type) {
    case 'venue':
      newFilters.venue = toggleArrayItem(value, newFilters.venue);
      break;
    case 'genre':
      newFilters.genre = toggleArrayItem(value, newFilters.genre);
      break;
    case 'city':
      newFilters.city = toggleArrayItem(value, newFilters.city);
      break;
    case 'artist':
      newFilters.artist = toggleArrayItem(value, newFilters.artist);
      break;
    default:
      break;
  }

  return newFilters;
};

export type EventState = {
  upcoming: Array<Event> | null,
  latest: Object<Event> | null,
  past: Array<Event> | null,
  pastPagination: { page: number, total: number, totalPages: number },
  gallery: Array<GalleryImageType> | null,
  all: Array<Event> | null,
  pastFilters: Array<EventFilters> | null,
  upcomingFilters: Array<EventFilters> | null,
  activeUpcomingFilters: Array<EventFilters> | null,
  activeFilters: Array<ActiveEventFilters> | null,
  artistsAndGenres: Array | null,
  overlayData: Array | null,
  banner: Array | null,
  eventArtistDetails: Array | null,
  totalStandingTickets: number | null,
  tableSelected: number | null,
  outlet_seating: Array | null,
  booked_standing_tickets: Object | null,
  event_booked_tables: Array | null,
  fnb_packages: Array | null,
  loading: {
    gallery: boolean,
    upcoming: boolean,
    past: boolean,
    pastFilters: boolean,
    pastMore: boolean,
    artistsAndGenres: boolean,
    overlayData: boolean,
    banner: boolean,
    outlet_seating: boolean,
    booked_standing_tickets: boolean,
    event_booked_tables: boolean,
    fnb_packages: boolean,
  },
  error: {
    gallery: string | null,
    upcoming: string | null,
    past: string | null,
    pastFilters: string | null,
    artistsAndGenres: string | null,
    overlayData: string | null,
    banner: string | null,
    outlet_seating: string | null,
    booked_standing_tickets: string | null,
    event_booked_tables: string | null,
    fnb_packages: string | null,
  },
};

const initialState: EventState = {
  upcoming: null,
  latest: null,
  past: null,
  pastPagination: { page: 1, total: 0, totalPages: 1 },
  all: null,
  pastFilters: null,
  upcomingFilters: null,
  activeFilters: null,
  activeUpcomingFilters: null,
  artistsAndGenres: null,
  overlayData: null,
  eventArtistDetails: null,
  totalStandingTickets: 0,
  tableSelected: null,
  outlet_seating: null,
  booked_standing_tickets: null,
  event_booked_tables: null,
  fnb_packages: null,
  loading: {
    upcoming: false,
    past: false,
    pastFilters: false,
    pastMore: false,
    all: false,
    artistsAndGenres: false,
    overlayData: false,
    outlet_seating: false,
    booked_standing_tickets: false,
    event_booked_tables: false,
    fnb_packages: false,
  },
  error: {
    upcoming: null,
    past: null,
    pastFilters: null,
    all: null,
    artistsAndGenres: false,
    overlayData: false,
    outlet_seating: false,
    booked_standing_tickets: false,
    event_booked_tables: false,
    fnb_packages: false,
  },
};

export const EventReducer = (
  state: EventState = initialState,
  action: FluxStandardAction
) => {
  const { type, payload, error } = action;
  switch (type) {
    case EventActionTypes.UPCOMING.FETCH:
      return {
        ...state,
        loading: { ...state.loading, upcoming: true },
        error: { ...state.error, upcoming: null },
      };
    case EventActionTypes.UPCOMING.SUCCESS:
      return {
        ...state,
        upcoming: payload,
        loading: { ...state.loading, upcoming: false },
      };
    case EventActionTypes.UPCOMING.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, upcoming: false },
        error: { ...state.error, upcoming: error },
      };
    case EventActionTypes.LATEST:
      return {
        ...state,
        latest: payload,
      };
    //  ALL Events
    case EventActionTypes.ALL.FETCH:
      return {
        ...state,
        loading: { ...state.loading, all: true },
        error: { ...state.error, all: null },
      };
    case EventActionTypes.ALL.SUCCESS:
      return {
        ...state,
        all: payload,
        loading: { ...state.loading, all: false },
      };
    case EventActionTypes.ALL.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, all: false },
        error: { ...state.error, all: error },
      };
    //  Past Events
    case EventActionTypes.PAST.FETCH:
      return {
        ...state,
        loading: { ...state.loading, past: true },
        error: { ...state.error, past: null },
      };
    case EventActionTypes.PAST.SUCCESS:
      return {
        ...state,
        past: payload.events,
        pastPagination: payload.pagination,
        loading: { ...state.loading, past: false, pastMore: false },
      };
    case EventActionTypes.PAST.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, past: false, pastMore: false },
        error: { ...state.error, past: error },
      };
    case EventActionTypes.PAST_APPEND_LOADING:
      return {
        ...state,
        loading: { ...state.loading, pastMore: true },
      };
    case EventActionTypes.PAST_APPEND:
      return {
        ...state,
        past: [...(state.past || []), ...payload.events],
        pastPagination: payload.pagination,
        loading: { ...state.loading, pastMore: false },
      };
    case EventActionTypes.PAST_FILTERS.FETCH:
      return {
        ...state,
        loading: { ...state.loading, pastFilters: true },
        error: { ...state.error, pastFilters: null },
      };
    case EventActionTypes.PAST_FILTERS.SUCCESS:
      return {
        ...state,
        pastFilters: payload,
        activeFilters: Object.assign({}, getInitialActiveFilterState()),
        loading: { ...state.loading, pastFilters: false },
      };
    case EventActionTypes.PAST_FILTERS.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, pastFilters: false },
        error: { ...state.error, pastFilters: error },
      };
    // Gallery
    case EventActionTypes.GALLERY.FETCH:
      return {
        ...state,
        loading: { ...state.loading, gallery: true },
        error: { ...state.error, gallery: null },
      };
    case EventActionTypes.GALLERY.SUCCESS:
      return {
        ...state,
        gallery: payload,
        loading: { ...state.loading, gallery: false },
      };
    case EventActionTypes.GALLERY.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, gallery: false },
        error: { ...state.error, gallery: error },
      };
    //  Past Events Filters
    case EventActionTypes.PAST_EVENT_FILTERS.INITIALIZE:
      return {
        ...state,
        pastFilters: payload,
        activeFilters: Object.assign({}, getInitialActiveFilterState()),
      };
    case EventActionTypes.PAST_EVENT_FILTERS.TOGGLE_FILTER:
      const activeFilters = updateActiveFilters(payload, state.activeFilters);
      return {
        ...state,
        activeFilters: { ...activeFilters },
      };
    case EventActionTypes.PAST_EVENT_FILTERS.CLEAR_FILTERS:
      return {
        ...state,
        activeFilters: Object.assign({}, getInitialActiveFilterState()),
      };
    //  Upcoming Events Filters
    case EventActionTypes.UPCOMING_EVENT_FILTERS.INITIALIZE:
      return {
        ...state,
        upcomingFilters: payload,
        activeUpcomingFilters: Object.assign(
          {},
          getInitialUpcomingActiveFilterState()
        ),
      };
    case EventActionTypes.UPCOMING_EVENT_FILTERS.TOGGLE_FILTER:
      const activeUpcomingFilters = updateActiveFilters(
        payload,
        state.activeUpcomingFilters
      );
      return {
        ...state,
        activeUpcomingFilters: { ...activeUpcomingFilters },
      };
    case EventActionTypes.UPCOMING_EVENT_FILTERS.CLEAR_FILTERS:
      return {
        ...state,
        activeUpcomingFilters: Object.assign(
          {},
          getInitialUpcomingActiveFilterState()
        ),
      };
    //  Past Events
    case EventActionTypes.ARTISTS_GENRES.FETCH:
      return {
        ...state,
        loading: { ...state.loading, past: true },
        error: { ...state.error, past: null },
      };
    case EventActionTypes.ARTISTS_GENRES.SUCCESS:
      return {
        ...state,
        past: payload,
        loading: { ...state.loading, past: false },
      };
    case EventActionTypes.ARTISTS_GENRES.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, past: false },
        error: { ...state.error, past: error },
      };
    // upcoming shows overlay data
    case EventActionTypes.OVERLAY_DATA.SUCCESS:
      return {
        ...state,
        overlayData: payload,
        loading: { ...state.loading, overlayData: false },
      };
    // event details banner
    case EventActionTypes.BANNER.FETCH:
      return {
        ...state,
        loading: { ...state.loading, banner: true },
        error: { ...state.error, banner: null },
      };
    case EventActionTypes.BANNER.SUCCESS:
      return {
        ...state,
        banner: payload,
        loading: { ...state.loading, banner: false },
      };
    case EventActionTypes.BANNER.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, banner: false },
        error: { ...state.error, banner: error },
      };
    case EventActionTypes.EVENT_ARTIST_DETAILS.FETCH:
      return {
        ...state,
        eventArtistDetails: payload,
      };
    case EventActionTypes.EVENT_TICKETS.STANDING:
      return {
        ...state,
        totalStandingTickets: payload,
      };
    case EventActionTypes.EVENT_TICKETS.TABLE_BOOKING:
      return {
        ...state,
        tableSelected: payload,
      };
    // outlet specific event tables
    case EventActionTypes.OUTLET_SEATING.FETCH:
      return {
        ...state,
        loading: { ...state.loading, outlet_seating: true },
        error: { ...state.error, outlet_seating: null },
      };
    case EventActionTypes.OUTLET_SEATING.SUCCESS:
      return {
        ...state,
        outlet_seating: payload,
        loading: { ...state.loading, outlet_seating: false },
      };
    case EventActionTypes.OUTLET_SEATING.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, outlet_seating: false },
        error: { ...state.error, outlet_seating: error },
      };
    //used to get the number of booked standing tickets for any given event at a particular time.
    case EventActionTypes.EVENT_STANDING_CAPACITY_STATUS.FETCH:
      return {
        ...state,
        loading: { ...state.loading, booked_standing_tickets: true },
        error: { ...state.error, booked_standing_tickets: null },
      };
    case EventActionTypes.EVENT_STANDING_CAPACITY_STATUS.SUCCESS:
      return {
        ...state,
        booked_standing_tickets: payload,
        loading: { ...state.loading, booked_standing_tickets: false },
      };
    case EventActionTypes.EVENT_STANDING_CAPACITY_STATUS.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, booked_standing_tickets: false },
        error: { ...state.error, booked_standing_tickets: error },
      };
    //used to get the booked tables for any given event at a particular time.
    case EventActionTypes.EVENT_BOOKED_TABLES.FETCH:
      return {
        ...state,
        loading: { ...state.loading, event_booked_tables: true },
        error: { ...state.error, event_booked_tables: null },
      };
    case EventActionTypes.EVENT_BOOKED_TABLES.SUCCESS:
      return {
        ...state,
        event_booked_tables: payload,
        loading: { ...state.loading, event_booked_tables: false },
      };
    case EventActionTypes.EVENT_BOOKED_TABLES.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, event_booked_tables: false },
        error: { ...state.error, event_booked_tables: error },
      };
    //used to get the fnb packages for any given event.
    case EventActionTypes.EVENT_FNB_PACKAGES.FETCH:
      return {
        ...state,
        loading: { ...state.loading, fnb_packages: true },
        error: { ...state.error, fnb_packages: null },
      };
    case EventActionTypes.EVENT_FNB_PACKAGES.SUCCESS:
      return {
        ...state,
        fnb_packages: payload,
        loading: { ...state.loading, fnb_packages: false },
      };
    case EventActionTypes.EVENT_FNB_PACKAGES.FAILURE:
      return {
        ...state,
        loading: { ...state.loading, fnb_packages: false },
        error: { ...state.error, fnb_packages: error },
      };
    default:
      return state;
  }
};
